import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  ListGroup,
  ListGroupItem,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import { ActividadUsuarioDto, SesionActividadDto } from "../../../types/ajustes";
import SectionState from "./SectionState";
import { requestJson } from "../../../helpers/api";
import { obtenerSesionContexto } from "../../../helpers/sesionesScope";

interface ActividadTabProps {
  actividades: ActividadUsuarioDto[];
  sesionesActivas: SesionActividadDto[];
  sesionesRecientes: SesionActividadDto[];
  ultimaActualizacion: number | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

const formatearDuracion = (segundos: number) => {
  if (!Number.isFinite(segundos)) return "0s";
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const segundosRestantes = Math.max(0, Math.floor(segundos % 60));
  const partes: string[] = [];
  if (horas > 0) partes.push(`${horas}h`);
  if (minutos > 0 || horas > 0) partes.push(`${minutos}m`);
  partes.push(`${segundosRestantes}s`);
  return partes.join(" ");
};

const formatearFecha = (fechaIso: string | null | undefined) => {
  if (!fechaIso) return "Sin registro";
  return new Date(fechaIso).toLocaleString();
};

const normalizar = (valor: string | null | undefined) => (valor ?? "").toLowerCase();
const describirDispositivo = (texto?: string | null) => {
  if (!texto) return "Dispositivo no identificado";
  if (texto.length <= 80) return texto;
  return `${texto.slice(0, 77)}...`;
};

const ActividadTab = ({
  actividades,
  sesionesActivas,
  sesionesRecientes,
  ultimaActualizacion,
  loading,
  error,
  onReload,
}: ActividadTabProps) => {
  const [search, setSearch] = useState("");
  const filtro = search.trim().toLowerCase();
  const [section, setSection] = useState<"resumen" | "sesiones" | "timeline" | "usuarios">(
    "resumen"
  );
  const [accionError, setAccionError] = useState<string | null>(null);
  const [cerrandoId, setCerrandoId] = useState<string | null>(null);
  const contextoSesion = obtenerSesionContexto();

  const actividadesFiltradas = useMemo(() => {
    if (!filtro) return actividades;
    return actividades.filter((item) => normalizar(item.nombreUsuario).includes(filtro));
  }, [actividades, filtro]);

  const sesionesActivasFiltradas = useMemo(() => {
    if (!filtro) return sesionesActivas;
    return sesionesActivas.filter((sesion) => {
      const nombre = normalizar(sesion.nombreUsuario);
      const ip = normalizar(sesion.ip);
      const metadata = (sesion.metadata as { userAgent?: string } | null) ?? null;
      const userAgent = metadata?.userAgent ? String(metadata.userAgent).toLowerCase() : "";
      return nombre.includes(filtro) || ip.includes(filtro) || userAgent.includes(filtro);
    });
  }, [sesionesActivas, filtro]);

  const sesionesRecientesFiltradas = useMemo(() => {
    if (!filtro) return sesionesRecientes;
    return sesionesRecientes.filter((sesion) => {
      const nombre = normalizar(sesion.nombreUsuario);
      const ip = normalizar(sesion.ip);
      return nombre.includes(filtro) || ip.includes(filtro);
    });
  }, [sesionesRecientes, filtro]);

  const sesionesTotales = actividades.reduce((acc, item) => acc + (item.sesionesTotales ?? 0), 0);
  const tiempoTotal = actividades.reduce(
    (acc, item) => acc + (item.tiempoTotalSegundos ?? 0),
    0
  );
  const usuariosActivos = actividades.filter((item) => item.activo).length;
  const usuariosInactivos = Math.max(0, actividades.length - usuariosActivos);
  const duracionPromedio =
    actividades.length > 0 ? Math.round(tiempoTotal / actividades.length) : 0;
  const ultimaActualizacionTexto = ultimaActualizacion
    ? new Date(ultimaActualizacion).toLocaleString()
    : "Sin actualización";
  const mostrarContenido = !loading && !error;

  const puedeCerrarSesion = (usuarioId: number | null) => {
    if (contextoSesion.permisos.includes("usuarios.cerrar-sesion")) {
      return true;
    }
    return (
      contextoSesion.usuarioId !== null &&
      usuarioId !== null &&
      Number(contextoSesion.usuarioId) === Number(usuarioId)
    );
  };

  const manejarCerrarSesion = async (sesionId: string, usuarioId: number | null) => {
    if (!puedeCerrarSesion(usuarioId)) {
      setAccionError("No tenés permisos para cerrar esta sesión.");
      return;
    }
    if (contextoSesion.sessionId && contextoSesion.sessionId === sesionId) {
      try {
        await requestJson("/api/auth/logout", { method: "POST" });
      } catch (logoutError) {
        console.error("Error al cerrar la propia sesión", logoutError);
      } finally {
        sessionStorage.removeItem("authUser");
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
      return;
    }
    setCerrandoId(sesionId);
    setAccionError(null);
    try {
      await requestJson(`/api/usuarios/sesiones/${sesionId}`, {
        method: "DELETE",
      });
      onReload();
    } catch (errorCerrar) {
      const mensaje =
        errorCerrar instanceof Error
          ? errorCerrar.message
          : "No se pudo cerrar la sesión seleccionada.";
      setAccionError(mensaje);
    } finally {
      setCerrandoId(null);
    }
  };

  const dispositivosActivosPorUsuario = useMemo(() => {
    const mapa = new Map<string, number>();
    sesionesActivasFiltradas.forEach((sesion) => {
      const clave =
        typeof sesion.usuarioId === "number" && !Number.isNaN(sesion.usuarioId)
          ? `u-${sesion.usuarioId}`
          : `n-${sesion.nombreUsuario ?? sesion.id}`;
      mapa.set(clave, (mapa.get(clave) ?? 0) + 1);
    });
    return mapa;
  }, [sesionesActivasFiltradas]);

  const obtenerDispositivosUsuario = (
    usuarioId: number | string | null | undefined,
    nombre: string | null,
  ) => {
    if (typeof usuarioId === "number" && !Number.isNaN(usuarioId)) {
      return dispositivosActivosPorUsuario.get(`u-${usuarioId}`) ?? 0;
    }
    return dispositivosActivosPorUsuario.get(`n-${nombre ?? "usuario"}`) ?? 0;
  };

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <div>
            <CardTitle tag="h5" className="mb-1">
              Actividad y presencia reciente
            </CardTitle>
            <p className="text-muted mb-0">Sesiones, acciones y últimos movimientos.</p>
            <small className="text-muted d-block mt-1">
              Actualizado: {ultimaActualizacionTexto}
            </small>
          </div>
          <Button color="light" size="sm" onClick={onReload}>
            Actualizar
          </Button>
        </div>

        <div className="mb-3">
          <Input
            type="search"
            placeholder="Filtrar por usuario o IP"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <SectionState
          loading={loading}
          error={error}
          description="Cargando actividad..."
          onRetry={onReload}
        />
        {accionError && (
          <div className="alert alert-danger py-2">
            <div className="d-flex justify-content-between align-items-center">
              <span>{accionError}</span>
              <button
                type="button"
                className="btn-close"
                aria-label="Cerrar"
                onClick={() => setAccionError(null)}
              />
            </div>
          </div>
        )}

        {mostrarContenido && (
          <>
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  href="#"
                  className={section === "resumen" ? "active" : ""}
                  onClick={(event) => {
                    event.preventDefault();
                    setSection("resumen");
                  }}
                >
                  Resumen
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className={section === "sesiones" ? "active" : ""}
                  onClick={(event) => {
                    event.preventDefault();
                    setSection("sesiones");
                  }}
                >
                  Sesiones activas
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className={section === "timeline" ? "active" : ""}
                  onClick={(event) => {
                    event.preventDefault();
                    setSection("timeline");
                  }}
                >
                  Línea de tiempo
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  href="#"
                  className={section === "usuarios" ? "active" : ""}
                  onClick={(event) => {
                    event.preventDefault();
                    setSection("usuarios");
                  }}
                >
                  Usuarios
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={section}>
              <TabPane tabId="resumen">
                <Row className="g-3 mb-3">
                  <Col xl="3" md="6">
                    <div className="border rounded p-3 h-100">
                      <p className="text-uppercase text-muted fs-12 mb-1">Usuarios activos</p>
                      <h4 className="mb-0">{usuariosActivos}</h4>
                    </div>
                  </Col>
                  <Col xl="3" md="6">
                    <div className="border rounded p-3 h-100">
                      <p className="text-uppercase text-muted fs-12 mb-1">Usuarios inactivos</p>
                      <h4 className="mb-0">{usuariosInactivos}</h4>
                    </div>
                  </Col>
                  <Col xl="3" md="6">
                    <div className="border rounded p-3 h-100">
                      <p className="text-uppercase text-muted fs-12 mb-1">Sesiones activas</p>
                      <h4 className="mb-0">{sesionesActivasFiltradas.length}</h4>
                    </div>
                  </Col>
                  <Col xl="3" md="6">
                    <div className="border rounded p-3 h-100">
                      <p className="text-uppercase text-muted fs-12 mb-1">Duración media</p>
                      <h4 className="mb-0">{formatearDuracion(duracionPromedio)}</h4>
                    </div>
                  </Col>
                </Row>
                <div className="d-flex flex-wrap gap-2 text-muted">
                  <Badge color="info" pill>
                    Sesiones totales: {sesionesTotales}
                  </Badge>
                  <Badge color="warning" pill>
                    Tiempo total: {formatearDuracion(tiempoTotal)}
                  </Badge>
                </div>
              </TabPane>

              <TabPane tabId="sesiones">
                {sesionesActivasFiltradas.length === 0 ? (
                  <p className="text-muted mb-0">No hay sesiones activas para el filtro.</p>
                ) : (
                  <ListGroup flush>
                    {sesionesActivasFiltradas.map((sesion) => {
                      const metadata = (sesion.metadata as { userAgent?: string } | null) ?? null;
                      const userAgent =
                        metadata && typeof metadata.userAgent === "string"
                          ? metadata.userAgent
                          : null;
                      return (
                        <ListGroupItem key={sesion.id} className="px-0">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-1">{sesion.nombreUsuario ?? "Usuario"}</h6>
                              <small className="text-muted">
                                Desde {formatearFecha(sesion.inicio)}
                              </small>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Badge color="info" pill>
                                {formatearDuracion(sesion.duracionSegundos)}
                              </Badge>
                              <Button
                                color="danger"
                                size="sm"
                                outline
                                disabled={
                                  cerrandoId === sesion.id ||
                                  !puedeCerrarSesion(sesion.usuarioId ?? null)
                                }
                                onClick={() =>
                                  manejarCerrarSesion(
                                    sesion.id,
                                    sesion.usuarioId ?? null,
                                  )
                                }
                                title={
                                  puedeCerrarSesion(sesion.usuarioId ?? null)
                                    ? undefined
                                    : "No tenés permisos para cerrar esta sesión."
                                }
                              >
                                {cerrandoId === sesion.id ? "Cerrando..." : "Cerrar"}
                              </Button>
                            </div>
                          </div>
                          <div className="text-muted small d-flex flex-column flex-lg-row gap-2">
                            <span>Último ping: {formatearFecha(sesion.ultimaActividad)}</span>
                            {sesion.ip && <span>IP: {sesion.ip}</span>}
                            {userAgent && (
                              <span className="text-truncate">
                                Dispositivo: {describirDispositivo(userAgent)}
                              </span>
                            )}
                          </div>
                        </ListGroupItem>
                      );
                    })}
                  </ListGroup>
                )}
              </TabPane>

              <TabPane tabId="timeline">
                {sesionesRecientesFiltradas.length === 0 ? (
                  <p className="text-muted mb-0">Sin registros para mostrar.</p>
                ) : (
                  <ListGroup flush>
                    {sesionesRecientesFiltradas.map((sesion) => (
                      <ListGroupItem key={sesion.id} className="px-0">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1">{sesion.nombreUsuario ?? "Usuario"}</h6>
                            <small className="text-muted">
                              Inicio: {formatearFecha(sesion.inicio)}
                            </small>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Badge color={sesion.activa ? "success" : "secondary"} pill>
                              {sesion.activa ? "En línea" : "Finalizada"}
                            </Badge>
                            <Button
                              color="danger"
                              size="sm"
                              outline
                              disabled={
                                !sesion.activa ||
                                cerrandoId === sesion.id ||
                                !puedeCerrarSesion(sesion.usuarioId ?? null)
                              }
                              onClick={() =>
                                manejarCerrarSesion(sesion.id, sesion.usuarioId ?? null)
                              }
                              title={
                                sesion.activa && !puedeCerrarSesion(sesion.usuarioId ?? null)
                                  ? "No tenés permisos para cerrar esta sesión."
                                  : undefined
                              }
                            >
                              {cerrandoId === sesion.id ? "Cerrando..." : "Cerrar"}
                            </Button>
                          </div>
                        </div>
                        <div className="text-muted small">
                          <div>
                            Último evento: {formatearFecha(sesion.ultimaActividad ?? sesion.fin)}
                          </div>
                          <div>Duración: {formatearDuracion(sesion.duracionSegundos)}</div>
                          {sesion.dispositivo && (
                            <div>Dispositivo: {describirDispositivo(sesion.dispositivo)}</div>
                          )}
                        </div>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                )}
              </TabPane>

              <TabPane tabId="usuarios">
                <Row className="g-3">
                  {actividadesFiltradas.length === 0 && (
                    <Col>
                      <div className="text-center text-muted py-4 border rounded">
                        No hay coincidencias para el filtro actual.
                      </div>
                    </Col>
                  )}
                  {actividadesFiltradas.map((item) => (
                    <Col
                      key={`${item.usuarioId}-${item.ultimoEventoFecha ?? "sin-evento"}`}
                      xl="4"
                      md="6"
                >
                  <div className="border rounded h-100 p-3">
                    <p className="text-uppercase text-muted fs-12 mb-1">
                      {item.nombreUsuario ?? "Usuario"}
                    </p>
                        <h3 className="mb-0">{item.sesionesTotales}</h3>
                        <p className="text-muted mb-0">Sesiones registradas</p>
                        <div className="mt-3">
                          <p className="text-muted mb-1 text-uppercase fs-12">Último evento</p>
                          <p className="mb-0">
                            {item.ultimoEventoAccion || (item.activo ? "Sesión activa" : "Sin datos")}
                          </p>
                          <small className="text-muted">{formatearFecha(item.ultimoEventoFecha)}</small>
                      <p className="mt-3 mb-1 fw-semibold">
                        Tiempo conectado: {formatearDuracion(item.tiempoTotalSegundos ?? 0)}
                      </p>
                      <p className="mb-1 text-muted">
                        Dispositivos activos:{" "}
                        {obtenerDispositivosUsuario(item.usuarioId, item.nombreUsuario)}
                      </p>
                      <Badge color={item.activo ? "success" : "secondary"} pill>
                        {item.activo ? "En línea" : "Offline"}
                      </Badge>
                    </div>
                  </div>
                    </Col>
                  ))}
                </Row>
              </TabPane>
            </TabContent>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ActividadTab;
